import { useEffect, useState, useCallback } from "react";
import { UserCog, Loader2, Save, KeyRound, ShieldCheck, Smartphone, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getProfile, updateProfileName, changePassword, reset2FA, confirm2FA } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Switch } from "@/components/ui/switch";
import { Sun, Moon } from "lucide-react";

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[var(--accent)]" />
      <h3 className="font-head font-bold text-sm">{title}</h3>
    </div>
    {children}
  </div>
);

export const Profile = () => {
  const { refresh } = useAuth();
  const { isLight, toggle } = useTheme();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  // 2FA
  const [twoFaPw, setTwoFaPw] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // {qr, secret}
  const [twoFaCode, setTwoFaCode] = useState("");

  const load = useCallback(async () => {
    try { const p = await getProfile(); setProfile(p); setName(p.name || ""); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (name.trim().length < 2) { toast.error("Ad en az 2 karakter olmalıdır."); return; }
    setSavingName(true);
    try { await updateProfileName(name.trim()); toast.success("Ad güncellendi."); await load(); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
    finally { setSavingName(false); }
  };

  const savePw = async () => {
    if (newPw.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalıdır."); return; }
    if (newPw !== newPw2) { toast.error("Yeni şifreler eşleşmiyor."); return; }
    setSavingPw(true);
    try {
      await changePassword(curPw, newPw);
      toast.success("Şifreniz güncellendi.");
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
    finally { setSavingPw(false); }
  };

  const startReset2fa = async () => {
    setTwoFaBusy(true);
    try {
      const res = await reset2FA(twoFaPw);
      setTwoFaSetup({ qr: res.qr, secret: res.secret });
      setTwoFaPw("");
      toast.info("Yeni QR kodu oluşturuldu. Authenticator uygulamanızla tarayın.");
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setTwoFaBusy(false); }
  };

  const confirmReset2fa = async () => {
    if (twoFaCode.length !== 6) { toast.error("6 haneli kodu girin."); return; }
    setTwoFaBusy(true);
    try {
      await confirm2FA(twoFaCode);
      toast.success("İki adımlı doğrulama yeniden kuruldu.");
      setTwoFaSetup(null); setTwoFaCode("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kod geçersiz"); }
    finally { setTwoFaBusy(false); }
  };

  const inp = "mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11";

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div className="flex items-center gap-2">
        <UserCog className="h-5 w-5 text-[var(--accent)]" />
        <h2 className="font-head font-bold text-xl">Profil</h2>
      </div>

      {profile && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--app-bg)] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)]">E-posta</div>
            <div className="font-mono text-sm text-[var(--text-primary)]" data-testid="profile-email">{profile.email}</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-1 rounded bg-[#A374FF]/10 text-[var(--accent)]">
            {profile.role === "admin" ? "YÖNETİCİ" : "KULLANICI"}
          </span>
        </div>
      )}

      <Card title="Görünüm (Tema)" icon={isLight ? Sun : Moon}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-[var(--text-primary)]" data-testid="theme-label">
              {isLight ? "Aydınlık Mod" : "Karanlık Mod"}
            </div>
            <p className="text-xs text-[var(--text-faint)] mt-0.5">Arayüz temasını değiştir</p>
          </div>
          <div className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-[var(--text-dim)]" />
            <Switch data-testid="theme-switch" checked={isLight} onCheckedChange={toggle}
              className="data-[state=checked]:bg-[var(--warn)]" />
            <Sun className="h-4 w-4 text-[var(--warn)]" />
          </div>
        </div>
      </Card>

      <Card title="Hesap Bilgileri" icon={UserCog}>
        <div>
          <Label className="text-xs text-[var(--text-dim)]">Ad Soyad</Label>
          <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className={inp} />
        </div>
        <Button data-testid="save-name" onClick={saveName} disabled={savingName}
          className="h-11 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
          {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Kaydet</>}
        </Button>
      </Card>

      <Card title="Şifre Değiştir" icon={KeyRound}>
        <div>
          <Label className="text-xs text-[var(--text-dim)]">Mevcut Şifre</Label>
          <Input data-testid="current-password" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className={inp} />
        </div>
        <div>
          <Label className="text-xs text-[var(--text-dim)]">Yeni Şifre</Label>
          <Input data-testid="new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inp} />
        </div>
        <div>
          <Label className="text-xs text-[var(--text-dim)]">Yeni Şifre (tekrar)</Label>
          <Input data-testid="new-password2" type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} className={inp} />
        </div>
        <Button data-testid="save-password" onClick={savePw} disabled={savingPw}
          className="h-11 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
          {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Şifreyi Güncelle</>}
        </Button>
      </Card>

      <Card title="İki Adımlı Doğrulama (2FA)" icon={ShieldCheck}>
        <div className="flex items-center gap-2 text-sm">
          <span className={`h-2 w-2 rounded-full ${profile?.totp_enabled ? "bg-[var(--pos)]" : "bg-[var(--text-faint)]"}`} />
          <span className="text-[var(--text-muted)]" data-testid="twofa-status">
            {profile?.totp_enabled ? "Aktif — Authenticator kodu ile korunuyor" : "Kurulum bekliyor"}
          </span>
        </div>

        {!twoFaSetup ? (
          <>
            <p className="text-xs text-[var(--text-faint)] leading-relaxed">
              Authenticator uygulamanızı kaybettiyseniz veya değiştirmek istiyorsanız, mevcut şifrenizi girerek 2FA'yı yeniden kurabilirsiniz.
            </p>
            <div>
              <Label className="text-xs text-[var(--text-dim)]">Mevcut Şifre</Label>
              <Input data-testid="twofa-password" type="password" value={twoFaPw} onChange={(e) => setTwoFaPw(e.target.value)} className={inp} />
            </div>
            <Button data-testid="reset-2fa" onClick={startReset2fa} disabled={twoFaBusy || twoFaPw.length < 6}
              variant="outline" className="h-11 border-[#A374FF]/40 text-[var(--accent)] hover:bg-[#A374FF]/10 hover:text-[var(--accent)]">
              {twoFaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCw className="h-4 w-4 mr-1.5" /> 2FA'yı Yeniden Kur</>}
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <Smartphone className="h-4 w-4" />
              <span className="text-sm font-bold">Yeni QR kodunu tarayın</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <img data-testid="twofa-qr" src={twoFaSetup.qr} alt="QR" className="h-40 w-40 rounded-lg bg-white p-2" />
              <code className="text-xs text-[var(--pos)] break-all">{twoFaSetup.secret}</code>
            </div>
            <div>
              <Label className="text-xs text-[var(--text-dim)]">6 Haneli Kod</Label>
              <Input data-testid="twofa-code" value={twoFaCode}
                onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000"
                className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-center text-xl tracking-[0.4em] h-12" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="twofa-confirm" onClick={confirmReset2fa} disabled={twoFaBusy}
                className="h-11 flex-1 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {twoFaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kurulumu Tamamla"}
              </Button>
              <Button variant="outline" onClick={() => { setTwoFaSetup(null); setTwoFaCode(""); }}
                className="h-11 border-[var(--border)] hover:bg-[var(--surface-hover)]">İptal</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
